'use client';

import { useEffect, useRef, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import type { TransactionsViewQuery } from '@bts-web/data-layer/server';
import { getTransactions } from '../gqlActions/getTransactions.action';
import { TransactionsWidgetSkeleton } from './TransactionsWidgetSkeleton';
import { WidgetTranslationsKeys } from './utils/getTransactionsWidgetTranslations';
import { itemsListHeaderStyles } from '../styles';
import { ContentSectionWrapper } from '../../layouts/ContentSectionWrapper/ContentSectionWrapper';
import { Link } from '../components';
import { TransactionList } from '../../transactions/TransactionList/TransactionList';
import { TransactionItemTranslationsKeys } from '../../transactions/types/transactions';
import { FilledArrowRight } from '../svgIcons';

type Transactions = NonNullable<
  NonNullable<TransactionsViewQuery['response']['transactions']>['edges']
>;

export type TransactionsWidgetVariant = 'Portfolio' | 'AssetDetails';

export interface TransactionsWidgetProps {
  assetId?: string;
  fetchNumberOfItems: number;
  variant: TransactionsWidgetVariant;
  showOnlyRedirectLink?: boolean;
  widgetTranslations: WidgetTranslationsKeys;
  translations: TransactionItemTranslationsKeys;
  locale: string;
}

const transactionsLinkStyles = css({
  gap: 'extra_small_2',
  fontSize: 'small',
  display: 'flex',
  alignItems: 'center',
});

const descriptionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'small',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
  color: 'neutrals.text_primary',
});

const wrapperStyles = css({ backgroundColor: 'neutrals.card_fill_primary' });

const transactionListStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'extra_small',
});

const transactionLinkWrapperStyles = css({ mt: 'extra_small' });

export const LazyLoadedTransactionsWidget = ({
  assetId,
  fetchNumberOfItems,
  variant,
  showOnlyRedirectLink = false,
  widgetTranslations,
  translations,
  locale,
}: TransactionsWidgetProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [transactions, setTransactions] = useState<Transactions | null>(null);

  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (showOnlyRedirectLink) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;

      observer.disconnect();

      getTransactions(
        {
          input: {
            ...(assetId && { assetId }),
            first: fetchNumberOfItems,
          },
        },
        false,
        true,
      )
        .then((result) => {
          const edges =
            (
              result?.data?.transactions as NonNullable<
                TransactionsViewQuery['response']['transactions']
              >
            )?.edges ?? [];

          if (assetId && !edges.length) {
            setHidden(true);

            return;
          }

          setTransactions(edges);
        })
        .catch(() => setHidden(true));
    });

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [assetId, fetchNumberOfItems, showOnlyRedirectLink]);

  if (hidden) {
    return null;
  }

  const transactionsLink = (
    <Link
      size="small"
      prefetch={false}
      href={assetId ? `/transactions?assetId=${assetId}` : '/transactions'}
    >
      <span className={transactionsLinkStyles}>
        {assetId
          ? widgetTranslations.viewRelatedTransactions
          : widgetTranslations.viewAllTransactions}

        <FilledArrowRight size={16} data-dir="rtl" />
      </span>
    </Link>
  );

  if (showOnlyRedirectLink) {
    return <ContentSectionWrapper>{transactionsLink}</ContentSectionWrapper>;
  }

  const noTransactionsToShow =
    transactions !== null && transactions.length === 0;

  return (
    <ContentSectionWrapper
      sectionTitle={
        variant === 'AssetDetails'
          ? widgetTranslations.viewRelatedTransactions
          : widgetTranslations.latestTransactions
      }
      {...(noTransactionsToShow ? { id: 'latest-transactions' } : {})}
      data-testid="transactions widget"
      className={wrapperStyles}
    >
      <div ref={sentinelRef} />
      {transactions === null ? (
        <TransactionsWidgetSkeleton />
      ) : noTransactionsToShow ? (
        <div className={descriptionStyles}>
          <p data-testid="empty-transactions">
            {widgetTranslations.emptyStateNoTransactions}
          </p>

          <p data-testid="latest-transactions-description">
            {widgetTranslations.latestTransactionsDescription}
          </p>
        </div>
      ) : (
        <div className={transactionListStyles}>
          <div className={itemsListHeaderStyles}>
            <span>{widgetTranslations.transactionDescription}</span>
            <span>{widgetTranslations.amount}</span>
          </div>
          <TransactionList
            transactions={transactions}
            translations={translations}
            locale={locale}
          />
          <div className={transactionLinkWrapperStyles}>{transactionsLink}</div>
        </div>
      )}
    </ContentSectionWrapper>
  );
};
